import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface InlineSpinnerProps {
  /** Accessible name announced by screen readers (e.g. "Saving"). */
  label?: string;
  /** Forwarded to `data-testid` so call sites can target the spinner in tests. */
  testId?: string;
  /** Extra classes merged after the muted-spinner defaults. */
  className?: string;
  /**
   * Render the spinner as purely decorative — `aria-hidden` with no
   * `role="status"`/`aria-label` — for use *beside* a visible loading message
   * that already conveys the state (a loading region / status line). Without
   * this, the announcing spinner and the visible text would double-announce the
   * same "loading" to assistive tech.
   */
  decorative?: boolean;
}

/**
 * The small muted spinner shown next to an inline control (e.g. a dropdown)
 * while a save is in flight — the "adjacent spinner" shape. The caller decides
 * *when* to render it (`{saving && <InlineSpinner … />}`); this component is only
 * the spinner itself. By default `role="status"` + `aria-label` make it announce;
 * pass `decorative` when it sits beside text that already announces the state.
 */
export function InlineSpinner({
  label = 'Loading',
  testId,
  className,
  decorative = false,
}: InlineSpinnerProps) {
  return (
    <Loader2
      role={decorative ? undefined : 'status'}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative || undefined}
      data-testid={testId}
      className={cn('h-4 w-4 animate-spin text-muted-foreground', className)}
    />
  );
}
